import { useCallback } from "react";
import {
    Dialog,
    DialogContent,
    IconButton,
    Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

interface PreviewDialogProps {
    open: boolean;
    onClose: () => void;
    fileUrl: string;
    fileName?: string;
}

const PdfPreviewDialog: React.FC<PreviewDialogProps> = ({
    open,
    onClose,
    fileUrl,
    fileName = "file",
}) => {
    const isExternal = fileUrl?.startsWith("http");

    // Download
    const handleDownload = useCallback(() => {
        if (!fileUrl) return;

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = fileName || "file";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [fileUrl, fileName]);

    const handleOpen = useCallback(() => {
        if (!fileUrl) return;
        window.open(fileUrl, "_blank", "noopener,noreferrer");
    }, [fileUrl]);

    const handleClose = () => {
        onClose();
    };

    if (!fileUrl) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            slotProps={{
                paper: {
                    sx: {
                        overflow: "hidden",
                        width: "65vw",
                        height: "85vh",
                        maxWidth: "95vw",
                        maxHeight: "95vh",
                    },
                }
            }}
        >
            <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-300">
                {/* File name */}
                <span className="text-lg font-medium truncate">
                    {fileName}
                </span>

                <div className="flex items-center gap-1">
                    {/* 👉 Open (always available) */}
                    <Tooltip title="Open in new tab">
                        <IconButton onClick={handleOpen}>
                            <OpenInNewIcon />
                        </IconButton>
                    </Tooltip>

                    {!isExternal && (
                        <Tooltip title="Download">
                            <IconButton onClick={handleDownload}>
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                    )}

                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </div>
            </div>

            <DialogContent
                className="flex flex-col bg-gray-100 overflow-hidden h-full"
                sx={{ p: 0 }}
            >
                <div className="relative flex items-center justify-center flex-1 overflow-hidden">
                    {/* PDF */}
                    <iframe
                        key={fileUrl}
                        src={`${fileUrl}#toolbar=0`}
                        className="w-full h-full border-0"
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default PdfPreviewDialog;